// RAID DIRECTOR — makes raids a regular part of play instead of only a punishment for neglect.
//
//   1. A scheduled raid every few minutes while you're actively playing.
//   2. Raids that follow real events (Drought, Bandits, Festival — see RaidDirectorConfig.fromEvents).
//   3. A danger level from the wealth you hold: bigger kingdom → bigger raids.
//   4. Rapid clicking on the clicker for 30 s straight brings a MASSIVE clan from one random edge (RaidDirectorConfig.fromClicking).
//
// THE RULE (the scribe never lies): every raid is announced ~20 s ahead with its REAL size and the REAL edge it will
// arrive from, then it happens exactly as announced, then a summary reports what really happened (goons slain,
// converted, retreated, sites damaged). The raid itself runs on the existing machinery: it emits Events.RaidStart /
// RaidEnd and the goon simulation in scene/wildGoons.js does the rest.

import { Events, on, emit } from '../ui/eventBus.js'
import { RaidDirectorConfig as CFG, FieldWorkConfig } from '../config/balance.js'
import { createRapidClicks, clickingClanDue } from './fieldWork.js'
import { getWealth } from './seasonManager.js'
import { damageCount } from './damageManager.js'
import { isOnTrip } from './capitalTrip.js'
import { isAdventuring } from './adventureManager.js'
import * as idle from './idleManager.js'
import { waveAlive, waveFleeing } from '../scene/wildGoons.js'
import { pushLog } from '../ui/hud.js'

// Edge index (see wildGoons.spawnOne) → compass name in the DEFAULT camera view (screen-up is north).
const EDGE_NAMES = ['north-west', 'south-east', 'north-east', 'south-west']

const rnd = (a, b) => a + Math.random() * (b - a)
const rndInt = (a, b) => Math.round(rnd(a, b))
const fmtS = (ms) => `${Math.max(0, Math.ceil(ms / 1000))} s`

let plan = null      // { id, reason, why, size, edge, phase: 'pending'|'warned'|'active', at, endsAt, ... }
let nextNatural = 0
let lastRaidEndAt = 0
let nextId = 1
let timer = null
let tally = null     // { slain, converted, sitesBefore }
const clicking = createRapidClicks(FieldWorkConfig) // the player's presses on the clicker, for the clicking-clan rule
let clanStreakUsed = null // the streak of rapid clicking that was already acted on (one clan per streak)
let presses = []          // press times within the last fromClicking.rapidMs (for the pace condition)

export function dangerTier(wealth = getWealth()) {
  let tier = CFG.tiers[0], idx = 0
  CFG.tiers.forEach((t, i) => { if (wealth >= t.minWealth) { tier = t; idx = i } })
  return { ...tier, index: idx }
}

export const raidState = () => (plan ? { phase: plan.phase, size: plan.size, edge: EDGE_NAMES[plan.edge], reason: plan.reason } : null)

// ── Gates: when is a raid allowed to be scheduled / start? ─────────────────────────────────────────────
function playerPlaying() {
  if (document.body.dataset.activePage !== 'play') return false
  if (isOnTrip() || isAdventuring()) return false          // those have their own raid rolls
  if (idle.isPaused() || idle.isFined()) return false
  if (idle.msSinceActivity() > 30_000) return false         // AFK raids are the idle system's job
  return true
}

function banner(data) { emit(Events.EventTriggered, { ...data, type: 'RAID' }) }

// ── Scheduling ────────────────────────────────────────────────────────────────────────────────────────
function schedule({ reason, why, delayMs, sizeMult = 1, minSize = 2, maxSize = Infinity }) {
  if (plan || Date.now() - lastRaidEndAt < CFG.minGapMs) return false
  const tier = dangerTier()
  const size = Math.min(maxSize, Math.max(2, minSize, Math.round(rndInt(tier.size[0], tier.size[1]) * sizeMult)))
  plan = { id: `raid${nextId++}`, reason, why, size, tier: tier.name, edge: Math.floor(Math.random() * 4), phase: 'pending', at: Date.now() + delayMs }
  return true
}

function warn() {
  plan.phase = 'warned'
  plan.at = Date.now() + CFG.warnMs
  const dir = EDGE_NAMES[plan.edge]
  const head = `${plan.size} goons from the ${dir}`
  banner({
    icon: '🔥', label: plan.reason === 'clicking' ? 'A goon clan approaches' : 'Raiders approaching',
    desc: `${head} · arriving in ${fmtS(CFG.warnMs)}`,
    action: 'Defend: walk Mao into them, or press 1 and click to shoot. Village mode guards your buildings; Fields mode guards your rice.',
    expiresAt: plan.at,
  })
  pushLog(`🔥 ${plan.why} ${head} — arriving in ${fmtS(CFG.warnMs)}. Walk Mao into them or press 1 and click to shoot.`)
}

function start() {
  plan.phase = 'active'
  plan.at = Date.now()
  plan.endsAt = Date.now() + CFG.maxDurationMs
  tally = { slain: 0, converted: 0, brawled: 0, sitesBefore: damageCount() }
  emit(Events.RaidStart, { source: 'wave', id: plan.id, size: plan.size, edge: plan.edge, reason: plan.reason })
  banner({ icon: '⚔', label: 'Raid!', desc: `${plan.size} goons are crossing the ${EDGE_NAMES[plan.edge]} edge`, action: 'Shoot or touch them before they torch your buildings.', expiresAt: Date.now() + 9_000 })
}

function end(timedOut) {
  const p = plan
  const fled = waveAlive(p.id) + waveFleeing(p.id)          // counted BEFORE the end: survivors retreat
  emit(Events.RaidEnd, { source: 'wave', id: p.id })       // (wildGoons.onRaidEnd)
  const sites = Math.max(0, damageCount() - tally.sitesBefore)
  const parts = [`${tally.slain} slain`]
  if (tally.brawled) parts.push(`${tally.brawled} brought down by your citizens`)
  if (tally.converted) parts.push(`${tally.converted} converted by your temple`)
  if (fled) parts.push(`${fled} retreated`)
  const desc = `${p.size} raiders: ${parts.join(', ')}`
  const action = sites ? `${sites} site${sites === 1 ? '' : 's'} damaged — press the REPAIR button on the left.` : 'Nothing was damaged.'
  banner({ icon: '🛡', label: 'Raid over', desc, action, expiresAt: Date.now() + 9_000 })
  pushLog(`🛡 Raid over — ${desc}. ${action}${timedOut && fled ? ' The rest are retreating — you can still shoot them as they leave.' : ''}`)
  plan = null
  tally = null
  lastRaidEndAt = Date.now()
  nextNatural = Date.now() + rnd(CFG.intervalMs[0], CFG.intervalMs[1])
}

// Rapid clicking for long enough: a massive clan. One per streak (stop for a moment and start again for another), and only
// while the player is really playing and no other raid is on — it never ambushes an AFK or away player.
function checkClickingClan(now) {
  const spec = CFG.fromClicking
  if (!CFG.enabled || !spec || plan) return
  const since = clicking.streakStart(now)
  if (since == null || since === clanStreakUsed || !playerPlaying()) return
  presses = presses.filter((t) => now - t <= spec.rapidMs)
  if (!clickingClanDue(spec, now - since, presses.length)) return
  clanStreakUsed = since
  schedule({ reason: 'clicking', why: spec.why, delayMs: 0, sizeMult: spec.sizeMult, minSize: spec.minSize, maxSize: spec.maxSize })
}

let suspended = false // dev/test: no raids are planned or advanced while set (window.__raids.suspend)
function tick() {
  if (suspended) return
  const now = Date.now()
  checkClickingClan(now)
  if (!plan) {
    if (!CFG.enabled || now < nextNatural || !playerPlaying()) return
    const tier = dangerTier()
    schedule({ reason: 'scheduled', why: `Word of your ${tier.name.toLowerCase()} kingdom has spread.`, delayMs: 0 })
    return
  }
  if (plan.phase === 'pending') {
    if (now < plan.at) return
    if (!playerPlaying()) { plan.at = now + 10_000; return } // wait until you're actually playing — never ambush an AFK or away player
    warn()
  } else if (plan.phase === 'warned') {
    if (now < plan.at) return
    if (!playerPlaying()) { plan.at = now + 10_000; return }
    start()
  } else if (plan.phase === 'active') {
    const left = waveAlive(plan.id)
    if (left === 0 || now >= plan.endsAt) end(left > 0)
  }
}

export function init() {
  nextNatural = Date.now() + rnd(CFG.firstRaidAfterMs[0], CFG.firstRaidAfterMs[1])
  if (timer) clearInterval(timer)
  timer = setInterval(tick, 1000)

  on('paw:click', () => { const t = Date.now(); clicking.click(t); presses.push(t) }) // the player's own press (the citizens' automatic clicks use Events.Click)

  // Real events can bring a raid.
  on(Events.EventTriggered, (e) => {
    if (!CFG.enabled || e.restored || e.type === 'RAID') return
    const spec = CFG.fromEvents[e.type]
    if (!spec || !playerPlaying() || Math.random() >= spec.chance) return
    schedule({ reason: e.type.toLowerCase(), why: spec.why, delayMs: spec.delayMs, sizeMult: spec.sizeMult })
  })

  // What really happened in the fight.
  const mine = (e) => tally && plan && e?.waveId === plan.id // only goons of THIS raid count
  on(Events.WildGoonSlain, (e) => { if (mine(e)) tally.slain += 1 })
  on(Events.WildGoonConverted, (e) => { if (mine(e)) tally.converted += 1 })
  on(Events.WildGoonBrawled, (e) => { if (mine(e)) tally.brawled += 1 })

  if (import.meta.env?.DEV) {
    window.__raids = {
      trigger: (reason = 'scheduled', size) => { plan = null; lastRaidEndAt = 0; schedule({ reason, why: 'Test raid.', delayMs: 0 }); if (size) plan.size = size; return raidState() },
      state: raidState, tier: dangerTier, streak: () => ({ ms: clicking.streakMs(Date.now()), used: clanStreakUsed, presses: presses.length }), now: () => ({ plan, nextNatural, lastRaidEndAt }),
      suspend: (on) => { suspended = !!on }, reset: () => { plan = null; tally = null; lastRaidEndAt = 0; clanStreakUsed = null }, skipWarning: () => { if (plan) plan.at = 0 }, endNow: () => { if (plan?.phase === 'active') end(true) },
    }
  }
}
